import React, { useState, useEffect, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { logStorageError } from '../../lib/firebase/logStorageError';
import SocialMediaConnect from './SocialMediaConnect';
import { TikTokIcon } from '../icons/TikTokIcon';
import { InstagramIcon } from '../icons/InstagramIcon';
import { YouTubeIcon } from '../icons/YouTubeIcon';
import EditIdentityModal from './EditIdentityModal';
import EditIconButton from './EditIconButton';

/**
 * IdentitySection - Hero-style identity card with per-field editing
 * Allows owners to edit name/handle, subtitle, location, platforms, and followers individually
 */

// Platform options with normalized keys
const PLATFORM_OPTIONS = [
  { key: 'tiktok', label: 'TikTok' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'youtube', label: 'YouTube' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'twitter', label: 'Twitter / X' },
  { key: 'snapchat', label: 'Snapchat' },
  { key: 'pinterest', label: 'Pinterest' },
  { key: 'linkedin', label: 'LinkedIn' },
];

// Normalize a single platform value to canonical key
function normalizePlatformKey(raw) {
  if (!raw) return null;
  const v = String(raw).trim().toLowerCase();

  if (v.includes('tiktok') || v === 'tt') return 'tiktok';
  if (v === 'ig' || v.includes('insta')) return 'instagram';
  if (v.includes('youtube') || v === 'yt') return 'youtube';
  if (v.includes('facebook') || v === 'fb') return 'facebook';
  if (v.includes('twitter') || v === 'x') return 'twitter';
  if (v.includes('snap')) return 'snapchat';
  if (v.includes('pinterest') || v === 'pin') return 'pinterest';
  if (v.includes('linkedin')) return 'linkedin';

  return v; // fallback to whatever it is, but lowercased
}

// Normalize and dedupe an array of platform values
function normalizePlatformArray(input) {
  if (!input) return [];
  const list = Array.isArray(input) ? input : [input];
  const normalized = list
    .map(normalizePlatformKey)
    .filter(Boolean);
  // dedupe while preserving order
  const seen = new Set();
  const result = [];
  normalized.forEach((key) => {
    if (!seen.has(key)) {
      seen.add(key);
      result.push(key);
    }
  });
  return result;
}

// Get human-readable label from key
function platformLabelFromKey(key) {
  const opt = PLATFORM_OPTIONS.find((o) => o.key === key);
  return opt ? opt.label : key;
}

/**
 * Compute total followers from socialAccounts (TikTok/Instagram scraped data).
 * Uses followersCount per platform so the total updates when scrapers run.
 *
 * @param {Object} socialAccounts - socialAccounts.tiktok, .instagram, etc.
 * @returns {number|null} - Sum of all platform followers, or null if no valid data
 */
function computeTotalFollowersFromSocialAccounts(socialAccounts) {
  if (!socialAccounts || typeof socialAccounts !== 'object') {
    return null;
  }
  let sum = 0;
  let hasValidData = false;
  const platforms = ['tiktok', 'instagram', 'youtube'];
  platforms.forEach((key) => {
    const account = socialAccounts[key];
    if (account && typeof account === 'object') {
      const count = account.followersCount;
      if (typeof count === 'number' && Number.isFinite(count) && count >= 0) {
        sum += count;
        hasValidData = true;
      }
    }
  });
  return hasValidData && sum > 0 ? sum : null;
}

/**
 * Compute total followers from platformStats (legacy/manual Platform Performance data).
 *
 * @param {Object} platformStats - Platform performance stats object
 * @returns {number|null} - Sum of all platform followers, or null if no valid data
 */
function computeTotalFollowers(platformStats) {
  if (!platformStats || typeof platformStats !== 'object') {
    return null;
  }

  let sum = 0;
  let hasValidData = false;

  Object.values(platformStats).forEach((stats) => {
    if (stats && typeof stats === 'object') {
      const followers = stats.followers;
      if (typeof followers === 'number' && Number.isFinite(followers) && followers >= 0) {
        sum += followers;
        hasValidData = true;
      }
    }
  });

  return hasValidData && sum > 0 ? sum : null;
}

function IdentitySection({
  identity,
  platformStats,
  socialAccounts, // Optional: scraped TikTok/Instagram etc. — total is computed from this first, then platformStats
  isOwner,
  avatarUrl,
  bannerImageUrl, // Optional banner image URL
  onSave,
  socialLinks: propSocialLinks, // Optional prop for social links
  contactEmail: propContactEmail, // Optional contact email for media kit
  onAvatarChange, // Optional avatar upload handler (File) => Promise<void>
  onAvatarRemove, // Optional avatar remove handler () => Promise<void>
  onBannerChange, // Optional banner upload handler (File) => Promise<void>
  onBannerRemove, // Optional banner remove handler () => Promise<void>
  userId, // Firebase Auth user ID (required for username claiming)
  showTotalFollowers = true, // Whether to show Total Followers (defaults to true for backward compatibility)
  profile, // Full profile object for the modal (optional, will be constructed if not provided)
  username, // Optional username for PDF filename
  userDoc, // Current user document (optional)
  socials, // Optional canonical socials array for public view
  onSocialsChange, // Optional (nextSocials) => void when user saves/removes from SocialMediaConnect
}) {
  // Safe defaults
  const safeIdentity = identity || {};
  
  // Normalize platforms immediately
  const normalizedPrimaryPlatforms = normalizePlatformArray(safeIdentity.primaryPlatforms);
  
  const {
    displayName = '',
    handle = '',
    creatorTitle = '',
    subtitle = '',
    niches = [],
    location = '',
    totalFollowersOverride,
  } = safeIdentity;

  // Total followers: prefer scraped socialAccounts (updates when TikTok/Instagram scrapers run), then legacy platformStats
  const totalFollowers = computeTotalFollowersFromSocialAccounts(socialAccounts) ?? computeTotalFollowers(platformStats);
  const hasValidFollowerData = totalFollowers != null && totalFollowers > 0;

  const ICON_MAP = {
    tiktok: TikTokIcon,
    instagram: InstagramIcon,
    youtube: YouTubeIcon,
  };

  const shortenUrl = (url) => {
    try {
      const parsed = new URL(url);
      return parsed.hostname.replace('www.', '') + parsed.pathname;
    } catch (error) {
      return url;
    }
  };

  const socialsToShow = Array.isArray(socials) ? socials : [];
  const hasSocials = socialsToShow.length > 0;

  // Modal state
  const [isIdentityModalOpen, setIsIdentityModalOpen] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [isUploadingBanner, setIsUploadingBanner] = useState(false);
  const [bannerError, setBannerError] = useState(null);
  const [avatarError, setAvatarError] = useState(null);
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
  const avatarInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const avatarMenuRef = useRef(null);

  // Crop modal state
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [cropMode, setCropMode] = useState('avatar'); // 'avatar' or 'banner'
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Construct profile object for modal if not provided
  const profileForModal = profile || {
    identity: safeIdentity,
    displayName,
    username: handle,
    creatorTitle,
    contactEmail: propContactEmail,
    email: propContactEmail,
    niche: subtitle ? subtitle.split(',').map(n => n.trim()).filter(Boolean) : [],
    location,
    platforms: normalizedPrimaryPlatforms.map(key => ({ platformName: key })),
    socialLinks: propSocialLinks || {},
  };

  // Auto-clean platforms if needed (legacy cleanup)
  useEffect(() => {
    const raw = identity?.primaryPlatforms;
    const normalized = normalizePlatformArray(raw);

    // Auto-clean if we detect difference and user owns profile
    // Only run once per unique raw value to avoid infinite loops
    if (
      isOwner &&
      onSave &&
      raw &&
      Array.isArray(raw) &&
      raw.length > 0 &&
      JSON.stringify(raw) !== JSON.stringify(normalized) &&
      normalized.length > 0
    ) {
      // Use a small delay to avoid race conditions and only run if component is still mounted
      const timeoutId = setTimeout(() => {
        onSave({ primaryPlatforms: normalized }).catch((err) => {
          console.warn('Failed to auto-clean legacy platforms:', err);
        });
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [identity?.primaryPlatforms, isOwner, onSave]);

  // Modal handlers
  const handleOpenIdentityModal = () => setIsIdentityModalOpen(true);
  const handleCloseIdentityModal = () => setIsIdentityModalOpen(false);

  // Handle saving name/handle
  // Handle file selection - open crop modal instead of directly uploading
  const handleAvatarFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      console.log('[IdentitySection] No file selected');
      return;
    }

    console.log('[IdentitySection] File selected for avatar upload:', {
      name: file.name,
      size: file.size,
      type: file.type,
    });

    // Clear any previous errors
    setAvatarError(null);

    const reader = new FileReader();
    reader.onload = () => {
      console.log('[IdentitySection] File read, opening crop modal');
      setSelectedImage(reader.result);
      setCropMode('avatar');
      setIsCropModalOpen(true);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null); // Reset - will be set when user interacts with cropper
    };
    reader.onerror = (err) => {
      console.error('[IdentitySection] Error reading file:', err);
      setAvatarError('Failed to read image file. Please try again.');
    };
    reader.readAsDataURL(file);

    // Reset input so the same file can be selected again if needed
    if (avatarInputRef.current) {
      avatarInputRef.current.value = '';
    }
  };

  const handleAvatarRemoveClick = async () => {
    if (!onAvatarRemove) return;
    setAvatarMenuOpen(false);
    setIsUploadingAvatar(true);
    try {
      await onAvatarRemove();
    } catch (e) {
      console.error('Error removing avatar:', e);
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  // Handle avatar menu actions
  const handleChangePhotoClick = () => {
    console.log('[IdentitySection] handleChangePhotoClick called', {
      hasInputRef: !!avatarInputRef.current,
      inputRef: avatarInputRef.current,
    });
    // Don't close the menu immediately - let the file picker open first
    // The menu will close when user selects a file or clicks outside
    if (avatarInputRef.current) {
      console.log('[IdentitySection] Clicking file input...');
      avatarInputRef.current.click();
      console.log('[IdentitySection] File input clicked');
      // Close menu after a short delay to allow file picker to open
      setTimeout(() => {
        setAvatarMenuOpen(false);
      }, 100);
    } else {
      console.error('[IdentitySection] avatarInputRef.current is null!');
    }
  };

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (avatarMenuRef.current && !avatarMenuRef.current.contains(event.target)) {
        setAvatarMenuOpen(false);
      }
    };

    if (avatarMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [avatarMenuOpen]);

  // Close menu on Escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && avatarMenuOpen) {
        setAvatarMenuOpen(false);
      }
    };

    if (avatarMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [avatarMenuOpen]);

  // Handle PDF download (print-to-PDF)
  const handleDownloadPDF = () => {
    // Store original title
    const originalTitle = document.title;
    
    // Set document title for PDF filename
    const profileName = safeIdentity.displayName || safeIdentity.handle || username || 'Profile';
    document.title = `${profileName} — InfluenceHub Media Kit`;
    
    // Trigger print dialog
    window.print();
    
    // Restore original title after a short delay (allows print dialog to read title)
    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  };

  // Banner handlers
  const handleBannerFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setBannerError('Image size must be less than 5MB');
      if (bannerInputRef.current) {
        bannerInputRef.current.value = '';
      }
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setBannerError('Please select an image file');
      if (bannerInputRef.current) {
        bannerInputRef.current.value = '';
      }
      return;
    }

    // Open crop modal instead of directly uploading
    const reader = new FileReader();
    reader.onload = () => {
      setSelectedImage(reader.result);
      setCropMode('banner');
      setIsCropModalOpen(true);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setBannerError(null);
    };
    reader.readAsDataURL(file);

    // Reset input so the same file can be selected again if needed
    if (bannerInputRef.current) {
      bannerInputRef.current.value = '';
    }
  };

  const handleBannerRemoveClick = async () => {
    if (!onBannerRemove) return;
    setIsUploadingBanner(true);
    setBannerError(null);
    try {
      await onBannerRemove();
    } catch (err) {
      console.error('Error removing banner:', err);
      setBannerError(err.message || 'Failed to remove banner image');
    } finally {
      setIsUploadingBanner(false);
    }
  };

  // Canvas cropping utility
  // For banner: outputWidth and outputHeight specify the final image dimensions (e.g., 1800x450)
  // For avatar: uses croppedAreaPixels dimensions directly
  const getCroppedImg = async (imageSrc, croppedAreaPixels, outputWidth = null, outputHeight = null) => {
    const image = new Image();
    image.src = imageSrc;
    await new Promise((resolve) => {
      image.onload = resolve;
    });

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('No 2d context');

    // For banner, scale to target dimensions; for avatar, use cropped dimensions
    if (outputWidth && outputHeight) {
      canvas.width = outputWidth;
      canvas.height = outputHeight;
      
      // Draw the cropped area, scaled to target dimensions
      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        outputWidth,
        outputHeight
      );
    } else {
      // Avatar: use cropped dimensions directly
      canvas.width = croppedAreaPixels.width;
      canvas.height = croppedAreaPixels.height;

      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        croppedAreaPixels.width,
        croppedAreaPixels.height
      );
    }

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        resolve(blob);
      }, 'image/jpeg', 0.9);
    });
  };

  // Handle crop complete - capture cropped area pixels
  // This is called by react-easy-crop whenever the user finishes adjusting the crop
  const handleCropComplete = (_, croppedPixels) => {
    if (croppedPixels) {
      setCroppedAreaPixels(croppedPixels);
    }
  };

  // Handle save cropped photo - generate cropped image and upload
  const handleSaveCroppedPhoto = async () => {
    if (!selectedImage || !croppedAreaPixels) return;
    let fileForLog = null;
    try {
      setIsSaving(true);
      
      if (cropMode === 'banner') {
        // Banner: crop to 4:1 aspect ratio, output at 1800x450 for crisp display
        if (!onBannerChange) return;
        
        setIsUploadingBanner(true);
        const blob = await getCroppedImg(selectedImage, croppedAreaPixels, 1800, 450);
        
        // Convert blob to File (uploadUserImage expects File)
        const file = new File([blob], 'banner.jpg', { type: 'image/jpeg' });
        fileForLog = file;
        
        // Call banner upload logic
        await onBannerChange(file);
        
        setIsCropModalOpen(false);
        setSelectedImage(null);
        setCroppedAreaPixels(null);
        setCropMode('avatar');
      } else {
        // Avatar: use cropped dimensions directly
        if (!onAvatarChange) return;
        
        setIsUploadingAvatar(true);
        setAvatarError(null);
        
        const blob = await getCroppedImg(selectedImage, croppedAreaPixels);
        
        // Convert blob to File (uploadUserImage expects File)
        const file = new File([blob], 'profile-photo.jpg', { type: 'image/jpeg' });
        fileForLog = file;
        
        // Call existing avatar upload logic
        await onAvatarChange(file);
        
        setIsCropModalOpen(false);
        setSelectedImage(null);
        setCroppedAreaPixels(null);
        setCropMode('avatar');
      }
    } catch (err) {
      const uploadPath = cropMode === 'banner'
        ? `uploads/${userId ?? 'unknown'}/banner.jpg`
        : `uploads/${userId ?? 'unknown'}/profile.jpg`;
      const uiError = logStorageError(err, {
        uid: userId ?? undefined,
        uploadPath,
        projectId: import.meta.env?.VITE_FIREBASE_PROJECT_ID,
        storageBucketFromConfig: import.meta.env?.VITE_FIREBASE_STORAGE_BUCKET,
        fileType: fileForLog?.type ?? 'image/jpeg',
        fileSizeBytes: fileForLog?.size,
        flow: `identity-section-cropper-${cropMode}`,
      });
      const errorMessage = `Failed to crop and upload ${cropMode} image: ${uiError}`;
      if (cropMode === 'banner') {
        setBannerError(errorMessage);
      } else {
        setAvatarError(errorMessage);
      }
    } finally {
      setIsSaving(false);
      setIsUploadingAvatar(false);
      setIsUploadingBanner(false);
    }
  };

  // Scroll to Social Links section in the modal
  const scrollToSocialLinks = () => {
    if (typeof document === 'undefined') return; // avoid SSR issues
    
    // If modal is not open, open it first
    if (!isIdentityModalOpen) {
      handleOpenIdentityModal();
      // Wait for modal to open and render, then scroll
      // Use multiple attempts to ensure the element is available
      const attemptScroll = (attempts = 0) => {
        const el = document.getElementById('social-links');
        if (el) {
          // Small delay to ensure modal animation has started
          setTimeout(() => {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 100);
        } else if (attempts < 10) {
          // Retry up to 10 times (500ms total)
          setTimeout(() => attemptScroll(attempts + 1), 50);
        }
      };
      // Start attempting after a short delay to let modal start opening
      setTimeout(() => attemptScroll(), 100);
    } else {
      // Modal is already open, just scroll
      const el = document.getElementById('social-links');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  return (
    <>
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm relative overflow-hidden">
      {/* A) Header wrapper: Banner container with relative positioning */}
      <div className="relative w-full">
        {/* Banner Image - LinkedIn style (4:1 aspect ratio) */}
        <div className="relative w-full aspect-[4/1] max-h-[112px] sm:max-h-[140px] overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200 rounded-t-xl">
          {bannerImageUrl ? (
            <img
              src={bannerImageUrl}
              alt="Profile banner"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-slate-100 via-slate-50 to-slate-200" />
          )}
          
          {/* Owner-only banner edit controls - top-right */}
          {isOwner && (
            <div className="absolute top-3 right-3 flex items-center gap-2 z-20">
              <input
                ref={bannerInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleBannerFileChange}
              />
              <EditIconButton
                onClick={() => bannerInputRef.current && bannerInputRef.current.click()}
                disabled={isUploadingBanner}
                isLoading={isUploadingBanner}
                title={bannerImageUrl ? 'Change banner' : 'Upload banner'}
              />
              {bannerImageUrl && onBannerRemove && (
                <button
                  type="button"
                  onClick={handleBannerRemoveClick}
                  disabled={isUploadingBanner}
                  className="p-2 bg-white/90 hover:bg-white rounded-full shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Remove banner"
                >
                  <svg className="w-4 h-4 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}
            </div>
          )}
        </div>

        {/* B) Avatar row - overlapping banner bottom edge */}
        <div className="absolute left-4 sm:left-8 -bottom-10 sm:-bottom-12 z-10">
          <div className="flex flex-col items-center gap-2" ref={avatarMenuRef}>
            {/* File input - MUST be outside dropdown so it doesn't get unmounted */}
            {isOwner && (
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  console.log('[IdentitySection] File input onChange fired!', {
                    files: e.target.files,
                    fileCount: e.target.files?.length,
                    firstFile: e.target.files?.[0],
                  });
                  handleAvatarFileChange(e);
                }}
                onClick={(e) => {
                  console.log('[IdentitySection] File input clicked');
                }}
              />
            )}
            
            {/* Avatar with hover overlay (owner-only) */}
            <div className="relative group">
              <div className="h-[100px] w-[100px] sm:h-[120px] sm:w-[120px] rounded-full bg-white border-4 border-white shadow-md overflow-hidden">
                {avatarUrl ? (
                  <img
                    key={avatarUrl} // Force re-render when URL changes
                    src={avatarUrl}
                    alt={displayName || handle || 'Creator avatar'}
                    className="h-full w-full object-cover"
                    onLoad={() => {
                      console.log('[IdentitySection] Avatar image loaded successfully:', avatarUrl);
                    }}
                    onError={(e) => {
                      console.error('[IdentitySection] Avatar image failed to load:', {
                        avatarUrl,
                        error: e,
                        timestamp: new Date().toISOString(),
                      });
                      // Don't show broken image, fall back to placeholder
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-slate-400">
                    <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                )}
              </div>
              
              {/* Hover overlay and action button (owner-only) - dropdown is a sibling to avoid button-in-button */}
              {isOwner && (
                <>
                  {/* Overlay */}
                  <div className="absolute inset-0 rounded-full bg-black/30 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity pointer-events-none" />
                  
                  {/* Action button - no nested buttons; dropdown rendered as sibling below */}
                  <button
                    type="button"
                    onClick={() => setAvatarMenuOpen(!avatarMenuOpen)}
                    onFocus={() => setAvatarMenuOpen(true)}
                    aria-label="Profile photo actions"
                    aria-expanded={avatarMenuOpen}
                    aria-haspopup="true"
                    className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                    </svg>
                  </button>

                  {/* Dropdown menu - sibling of trigger button to avoid invalid button > button */}
                  {avatarMenuOpen && (
                    <div className="absolute left-0 top-full mt-2 w-40 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50" role="menu">
                      <button
                        type="button"
                        role="menuitem"
                        onClick={(e) => {
                          console.log('[IdentitySection] Upload photo button clicked');
                          e.preventDefault();
                          e.stopPropagation();
                          handleChangePhotoClick();
                        }}
                        disabled={isUploadingAvatar}
                        className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {avatarUrl ? (isUploadingAvatar ? 'Updating…' : 'Change photo') : (isUploadingAvatar ? 'Uploading…' : 'Upload photo')}
                      </button>
                      {avatarUrl && onAvatarRemove && (
                        <button
                          type="button"
                          role="menuitem"
                          onClick={handleAvatarRemoveClick}
                          disabled={isUploadingAvatar}
                          className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Remove photo
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Banner error message (owner-only) */}
      {isOwner && bannerError && (
        <div className="px-4 sm:px-6 md:px-7 pt-2">
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {bannerError}
          </div>
        </div>
      )}

      {/* Avatar error message (owner-only) */}
      {isOwner && avatarError && (
        <div className="px-4 sm:px-6 md:px-7 pt-2">
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {avatarError}
            <button
              onClick={() => setAvatarError(null)}
              className="ml-2 text-red-800 hover:text-red-900 underline"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* C) Content block - starts below avatar overlap area */}
      <div className="pt-12 sm:pt-14 pb-4 sm:pb-6 md:pb-7 px-4 sm:px-6 md:px-7 relative">
      {/* Soft brand gradient background */}
      <div className="pointer-events-none absolute inset-0 opacity-70">
        <div
          className="h-full w-full bg-[radial-gradient(circle_at_top_left,_rgba(27,157,162,0.06),_transparent_55%),_radial-gradient(circle_at_bottom_right,_rgba(120,66,214,0.07),_transparent_55%)]"
        />
      </div>
      
      {/* Edit button - top-right of white card */}
      {isOwner && (
        <div className="absolute right-4 sm:right-6 md:right-7 top-4 sm:top-6 z-10">
          <EditIconButton onClick={handleOpenIdentityModal} />
        </div>
      )}
        
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
        {/* Left: Identity Text */}
        <div className="flex flex-col flex-1 min-w-0 w-full sm:w-auto text-center sm:text-left">
            {/* Name + Handle + Creator Title */}
              <div className="flex flex-col gap-2">
              <div className="flex flex-col">
                {/* Name only - Edit button moved to top-right */}
                <div className="flex items-center justify-center sm:justify-start">
                  <div className="text-2xl sm:text-3xl font-bold text-slate-900 break-words">
                      {displayName || 'Unnamed creator'}
                  </div>
                </div>
                <div className="text-sm text-slate-500">
                  {handle ? `@${handle}` : 'No handle set'}
                </div>
                {/* Creator Title on its own line below username */}
                {creatorTitle && creatorTitle.trim() && (
                  <div className="text-sm text-slate-600 mt-1">
                    {creatorTitle}
                  </div>
                  )}
                </div>

                {/* Email */}
                {propContactEmail && (
                  <div className="text-sm text-slate-600 break-words">
                    <a
                      href={`mailto:${propContactEmail}`}
                      className="text-slate-600 hover:text-slate-900 hover:underline break-all"
                    >
                      {propContactEmail}
                    </a>
                  </div>
                )}

              {/* Niche */}
              {(subtitle || (Array.isArray(niches) && niches.length > 0)) && (
                <div className="text-sm text-slate-600 break-words px-2 sm:px-0">
                  {subtitle || (Array.isArray(niches) ? niches.join(' • ') : '')}
                  </div>
                )}

                {/* Location */}
              {location && (
                <div className="text-sm text-slate-600 break-words">
                  {location}
                      </div>
                    )}
                    
              {/* Social presence */}
              {isOwner && (
                <SocialMediaConnect userDoc={userDoc} userId={userId} onSocialsChange={onSocialsChange} />
              )}
              {!isOwner && hasSocials && (
                <div className="mt-3 flex flex-wrap gap-2 justify-center sm:justify-start">
                  {socialsToShow.map((social) => {
                    const Icon = ICON_MAP[social.platform];
                    return (
                      <a
                        key={social.platform}
                        href={social.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:border-slate-300 hover:text-[#1B9DA2]"
                      >
                        {Icon ? <Icon className="h-4 w-4 text-slate-700" /> : null}
                        {social.handle ? `@${social.handle}` : shortenUrl(social.url)}
                      </a>
                    );
                  })}
                </div>
              )}
              </div>
        </div>

        {/* Right: Total Followers - Only shown if valid Platform Performance data exists AND showTotalFollowers is true */}
        {hasValidFollowerData && showTotalFollowers && (
          <div className="flex flex-col items-center sm:items-start md:items-end gap-1 flex-shrink-0 w-full sm:w-auto pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-200">
            {/* PDF Download button (public view only) */}
            {!isOwner && (
              <button
                type="button"
                onClick={handleDownloadPDF}
                aria-label="Download media kit as PDF"
                title="Download media kit as PDF"
                className="mb-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 hover:border-slate-400 hover:text-slate-900 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 cursor-pointer no-print"
              >
                <svg className="w-4 h-4 sm:w-[18px] sm:h-[18px] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 10.5v6m0 0l-3-3m3 3l3-3" />
                </svg>
                <span>Download PDF</span>
              </button>
            )}
            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Total followers
            </div>
            <div className="text-xl sm:text-xl md:text-2xl font-semibold text-slate-900">
              {totalFollowers.toLocaleString()}
            </div>
            {isOwner && (
              <p className="text-[11px] text-slate-500 text-center sm:text-left md:text-right">
                Calculated from Platform Performance
              </p>
            )}
          </div>
        )}
      </div>
      </div>
    </section>

    {/* Edit Identity Modal */}
    {isOwner && (
      <EditIdentityModal
        isOpen={isIdentityModalOpen}
        onClose={handleCloseIdentityModal}
        profile={profileForModal}
        onSave={onSave}
        userId={userId}
      />
    )}

    {/* Profile Photo / Banner Crop Modal */}
    {isOwner && (
      <ProfilePhotoCropModal
        isOpen={isCropModalOpen}
        imageSrc={selectedImage}
        crop={crop}
        zoom={zoom}
        cropMode={cropMode}
        onCropChange={setCrop}
        onZoomChange={setZoom}
        onCropComplete={handleCropComplete}
        onCancel={() => {
          if (isSaving) return;
          setIsCropModalOpen(false);
          setSelectedImage(null);
          setCroppedAreaPixels(null);
          setCropMode('avatar');
        }}
        onSave={handleSaveCroppedPhoto}
        isSaving={isSaving}
      />
    )}
  </>
  );
}

// Profile Photo / Banner Crop Modal Component
const ProfilePhotoCropModal = ({
  isOpen,
  imageSrc,
  crop,
  zoom,
  cropMode = 'avatar', // 'avatar' or 'banner'
  onCropChange,
  onZoomChange,
  onCropComplete,
  onCancel,
  onSave,
  isSaving,
}) => {
  if (!isOpen || !imageSrc) return null;

  const isBanner = cropMode === 'banner';
  const aspect = isBanner ? 4 / 1 : 1;
  const cropShape = isBanner ? 'rect' : 'round';
  const title = isBanner ? 'Adjust your banner' : 'Adjust your profile photo';
  const helperText = isBanner 
    ? 'Banner crops to 4:1 (recommended 450×112). Drag to reposition and zoom.'
    : 'Drag to reposition and use the slider to zoom. Your photo will be cropped to a circle.';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-xl mx-4">
        <h2 className="text-base font-semibold text-slate-900">
          {title}
        </h2>
        <p className="mt-1 text-xs text-slate-500">
          {helperText}
        </p>

        <div className={`mt-4 relative ${isBanner ? 'h-48' : 'h-64'} w-full bg-slate-100 rounded-xl overflow-hidden`}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            cropShape={cropShape}
            showGrid={false}
            onCropChange={onCropChange}
            onZoomChange={onZoomChange}
            onCropComplete={onCropComplete}
          />
        </div>

        <div className="mt-4 flex items-center gap-3">
          <span className="text-xs text-slate-500">Zoom</span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => onZoomChange(Number(e.target.value))}
            className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
          />
          <span className="text-xs text-slate-500 w-8 text-right">{zoom.toFixed(1)}x</span>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="text-sm font-medium text-slate-500 hover:text-slate-700 px-4 py-2 rounded-lg transition-colors"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSave}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : isBanner ? 'Save banner' : 'Save photo'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default IdentitySection;
